import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "seller" ? "seller" : "buyer");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", phone: "", city: "", country: "", newsletter_opt_in: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await register({ ...form, role });
      toast.success(t("reg.legacy.success"));
      nav(role === "seller" ? "/vendeur" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reg.legacy.error"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="register-page">
      <form onSubmit={onSubmit} className="w-full max-w-lg space-y-5 bg-white p-8 lg:p-10 rounded-lg border border-[#1A3322]/10" data-testid="register-form">
        <div>
          <h1 className="heading font-extrabold text-3xl text-[#111812]">{t("reg.legacy.title")}</h1>
          <p className="text-sm text-[#4B5563] mt-2">{t("reg.legacy.subtitle")}</p>
        </div>

        <div className="space-y-2">
          <Label>{t("reg.legacy.iam")}</Label>
          <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
            <label htmlFor="r-buyer" className={`border rounded-lg p-4 cursor-pointer ${role==="buyer"?"border-[#1A3322] bg-[#F5F5F0]":"border-[#1A3322]/15"}`}>
              <RadioGroupItem id="r-buyer" value="buyer" className="sr-only" data-testid="role-buyer"/>
              <p className="font-semibold">{t("reg.legacy.buyer")}</p>
              <p className="text-xs text-[#4B5563] mt-1">{t("reg.legacy.buyer_desc")}</p>
            </label>
            <label htmlFor="r-seller" className={`border rounded-lg p-4 cursor-pointer ${role==="seller"?"border-[#1A3322] bg-[#F5F5F0]":"border-[#1A3322]/15"}`}>
              <RadioGroupItem id="r-seller" value="seller" className="sr-only" data-testid="role-seller"/>
              <p className="font-semibold">{t("reg.legacy.seller")}</p>
              <p className="text-xs text-[#4B5563] mt-1">{t("reg.legacy.seller_desc")}</p>
            </label>
          </RadioGroup>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2 col-span-2">
            <Label>{t("reg.legacy.fullname")}</Label>
            <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="reg-fullname"/>
          </div>
          <div className="space-y-2 col-span-2">
            <Label>{t("reg.legacy.email")}</Label>
            <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email"/>
          </div>
          <div className="space-y-2 col-span-2">
            <Label>{t("reg.legacy.password")}</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password"/>
          </div>
          <div className="space-y-2">
            <Label>{t("reg.legacy.city")}</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="reg-city"/>
          </div>
          <div className="space-y-2">
            <Label>{t("reg.legacy.country")}</Label>
            <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="reg-country"/>
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-11" data-testid="reg-submit">
          {loading ? t("reg.legacy.loading") : t("reg.legacy.submit")}
        </Button>

        <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="reg-newsletter-label">
          <input
            type="checkbox"
            checked={form.newsletter_opt_in}
            onChange={(e) => set("newsletter_opt_in", e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#C85A32]"
            data-testid="reg-newsletter-checkbox"
          />
          <span className="text-xs text-[#4B5563] leading-relaxed">
            {t("reg.newsletter") || "Je souhaite recevoir les nouveautés, arrivages, promotions et offres de SUKANACOOP par email. Je peux me désabonner à tout moment depuis mon compte."}
          </span>
        </label>

        <p className="text-sm text-center text-[#4B5563]">
          {t("reg.already")} <Link to="/connexion" className="text-[#1A3322] font-semibold" data-testid="to-login">{t("rs.login")}</Link>
        </p>
      </form>
    </div>
  );
}
